// Shared admin chrome — sidebar nav + content region — used by both the
// platform admin (/admin/*) and every group-scoped admin (/circles/[id]/admin,
// /networks/[id]/admin, /groups/[id]/admin). One shell, two callers; the
// only difference is whether a `backHref` breadcrumb is provided.
//
// Layout: optional top breadcrumb (back-link only, group-scoped admins
// only) sits above a two-column body (SidebarNav | scrolling content). The
// shell fills its parent (AppShell `<main>`, which has a definite height
// via flex: 1). `minHeight: 0` on the flex chain lets the inner
// overflow:auto resolve correctly instead of pushing the column past the
// viewport.

import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import {
  Body, I, Icon, Row, SidebarNav, Stack,
  type SidebarItem,
} from '@leanin/ui';
import { RouterLinkAs } from '@/lib/router-link';
import styles from './AdminShell.module.css';

type Props = {
  // Optional back-link breadcrumb. Group-scoped admins pass both; platform
  // admin omits both and no top bar renders. `backLabel` is a ReactNode so
  // callers can drop a `<Skeleton/>` placeholder in while the group's name
  // is still loading.
  readonly backHref?: string;
  readonly backLabel?: ReactNode;
  readonly items: ReadonlyArray<SidebarItem>;
  readonly active: string;
  readonly children: ReactNode;
};

export function AdminShell({
  backHref,
  backLabel,
  items,
  active,
  children,
}: Props) {
  const hasBack = backHref !== undefined && backLabel !== undefined;
  return (
    <Stack gap="none" style={{ height: '100%', minHeight: 0 }}>
      {hasBack ? (
        <Row
          py="md"
          px="xl"
          style={{
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
          }}
        >
          <Link
            to={backHref}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Row gap="xs" align="center">
              <Icon size={14} color="var(--muted-foreground)">{I.back}</Icon>
              <Body size="sm" color="mute">{backLabel}</Body>
            </Row>
          </Link>
        </Row>
      ) : null}

      <div className={styles.body}>
        <aside className={styles.sidebar}>
          <SidebarNav
            items={items as SidebarItem[]}
            active={active}
            linkAs={RouterLinkAs}
          />
        </aside>
        <Stack
          gap="none"
          p="xl"
          className={styles.content}
          style={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto' }}
        >
          {children}
        </Stack>
      </div>
    </Stack>
  );
}
